import { createReactiveSignal } from './reactive.js';
import { disconnectSignal, getSignal } from './get.js';
import { dirtySignal, dirtySignalConsumers } from './dirty.js';
import { scheduleAsyncPull, trackPendingStart, trackPendingEnd } from './scheduling.js';
import { createEdge, findAndRemoveDirty } from './edge.js';
import { signal } from './signal.js';
import { DEFAULT_EQUALS, equalsFrom } from './utils/equals.js';
import { getCurrentConsumer } from './consumer.js';
import { createCallback } from './callback.js';
import { getTracerProxy, TracerEventType } from './trace.js';
const arrayFrom = Array.from;
function isThenable(v) {
    return v !== null && typeof v === 'object' && typeof v.then === 'function';
}
function thenLoop(v, onFulfill, onReject) {
    if (isThenable(v)) {
        v.then(onFulfill, onReject);
    }
    else {
        onFulfill(v);
    }
}
export class ReactivePromiseImpl {
    _value = undefined;
    _error = undefined;
    _flags = 1 /* AsyncFlags.Pending */;
    _signal = undefined;
    _equals = DEFAULT_EQUALS;
    _promise;
    _pending = [];
    _stateSubs = new Map();
    _awaitSubs = new Map();
    _updatedCount = 0;
    // Version is not really needed in a pure signal world, but when integrating
    // with non-signal code, it's sometimes needed to entangle changes to the promise.
    // For example, in React we need to entangle each promise immediately after it
    // was used because we can't dynamically call hooks.
    _version = signal(0);
    // Private but exposed for the ReactiveTask interface so we don't have to create a new
    // class and make all this code polypmorphic
    run = undefined;
    constructor(executor) {
        setReactivePromise(this);
        // If an executor is provided, behave like Promise constructor
        if (executor) {
            const resolve = (value) => {
                if (value && typeof value.then === 'function') {
                    this._setPromise(value);
                }
                else {
                    this._setValue(value);
                }
            };
            const reject = (reason) => {
                this._setError(reason);
            };
            try {
                executor(resolve, reject);
            }
            catch (e) {
                reject(e);
            }
        }
    }
    static all(values) {
        const p = new ReactivePromiseImpl();
        const arr = arrayFrom(values);
        const len = arr.length;
        if (len === 0) {
            p._setValue([]);
            return p;
        }
        const results = new Array(len);
        let remaining = len;
        let rejected = false;
        const onFulfillAt = (i) => (v) => {
            if (rejected)
                return;
            results[i] = v;
            if (--remaining === 0)
                p._setValue(results);
        };
        const onReject = (r) => {
            if (rejected)
                return;
            rejected = true;
            p._setError(r);
        };
        for (let i = 0; i < len; i++) {
            thenLoop(arr[i], onFulfillAt(i), onReject);
        }
        return p;
    }
    static race(values) {
        const p = new ReactivePromiseImpl();
        const arr = arrayFrom(values);
        const len = arr.length;
        if (len === 0)
            return p;
        let settled = false;
        const onFulfill = (v) => {
            if (settled)
                return;
            settled = true;
            p._setValue(v);
        };
        const onReject = (r) => {
            if (settled)
                return;
            settled = true;
            p._setError(r);
        };
        for (let i = 0; i < len; i++) {
            thenLoop(arr[i], onFulfill, onReject);
        }
        return p;
    }
    static any(values) {
        const p = new ReactivePromiseImpl();
        const arr = arrayFrom(values);
        const len = arr.length;
        if (len === 0) {
            // Like native Promise.any([]): reject with AggregateError
            p._setError(new AggregateError([], 'No promises were provided to ReactivePromise.any'));
            return p;
        }
        let pending = len;
        const errors = new Array(len);
        let fulfilled = false;
        const onFulfill = (value) => {
            if (fulfilled)
                return;
            fulfilled = true;
            p._setValue(value);
        };
        const onRejectAt = (index) => (reason) => {
            if (fulfilled)
                return;
            errors[index] = reason;
            if (--pending === 0) {
                p._setError(new AggregateError(errors, 'All promises were rejected in ReactivePromise.any'));
            }
        };
        for (let i = 0; i < len; i++) {
            thenLoop(arr[i], onFulfill, onRejectAt(i));
        }
        return p;
    }
    static allSettled(values) {
        const p = new ReactivePromiseImpl();
        const arr = arrayFrom(values);
        const len = arr.length;
        if (len === 0) {
            p._setValue([]);
            return p;
        }
        const results = new Array(len);
        let remaining = len;
        const onFulfillAt = (index) => (value) => {
            results[index] = { status: 'fulfilled', value };
            if (--remaining === 0)
                p._setValue(results);
        };
        const onRejectAt = (index) => (reason) => {
            results[index] = { status: 'rejected', reason };
            if (--remaining === 0)
                p._setValue(results);
        };
        for (let i = 0; i < len; i++) {
            thenLoop(arr[i], onFulfillAt(i), onRejectAt(i));
        }
        return p;
    }
    static resolve(value) {
        if (value instanceof ReactivePromiseImpl)
            return value;
        return new ReactivePromiseImpl(resolve => resolve(value));
    }
    static reject(reason) {
        return new ReactivePromiseImpl((_resolve, reject) => reject(reason));
    }
    static withResolvers() {
        const p = new ReactivePromiseImpl();
        p._equals = DEFAULT_EQUALS;
        p._initFlags(1 /* AsyncFlags.Pending */);
        const resolve = (value) => {
            if (value && typeof value.then === 'function') {
                p._setPromise(value);
            }
            else {
                p._setValue(value);
            }
        };
        const reject = (reason) => {
            p._setError(reason);
        };
        return { promise: p, resolve, reject };
    }
    _initFlags(baseFlags) {
        if (IS_DEV) {
            const tracer = getTracerProxy();
            if (tracer !== undefined && this._signal !== undefined && (baseFlags & 1 /* AsyncFlags.Pending */) !== 0) {
                tracer.emit({
                    type: TracerEventType.StartLoading,
                    id: this._signal.tracerMeta.id,
                });
            }
        }
        if (IS_LOCAL_DEV) {
            if (baseFlags & 1 /* AsyncFlags.Pending */) {
                trackPendingStart();
            }
        }
        this._flags = baseFlags;
    }
    _consumeFlags(flags) {
        const currentConsumer = getCurrentConsumer();
        if (currentConsumer === undefined)
            return;
        if ((this._flags & 128 /* AsyncFlags.isRelay */) !== 0) {
            this._connect();
        }
        const ref = currentConsumer.ref;
        const subs = this._stateSubs;
        const subbedFlags = subs.get(ref) ?? 0;
        subs.set(ref, subbedFlags | flags);
    }
    _connect() {
        const signal = this._signal;
        const currentConsumer = getCurrentConsumer();
        // A relay's update function may read its own ReactivePromise (e.g. to check
        // isPending/value). Skip self-subscription to avoid a dependency cycle.
        if (currentConsumer === signal)
            return;
        if (currentConsumer?.watchCount === 0) {
            const { ref, computedCount, deps } = currentConsumer;
            const prevEdge = deps.get(signal);
            if (prevEdge?.consumedAt !== computedCount) {
                const newEdge = createEdge(prevEdge, 0 /* EdgeType.Signal */, signal, signal.updatedCount, computedCount);
                signal.subs.set(ref, newEdge);
                deps.set(signal, newEdge);
            }
        }
        else {
            getSignal(signal);
        }
    }
    _setFlags(setTrue, setFalse = 0, notify = 0) {
        const prevFlags = this._flags;
        const nextFlags = (prevFlags & ~setFalse) | setTrue;
        const allChanged = (prevFlags ^ nextFlags) | notify;
        this._flags = nextFlags;
        if (IS_LOCAL_DEV) {
            const pendingChanged = allChanged & 1 /* AsyncFlags.Pending */;
            if (pendingChanged) {
                if (nextFlags & 1 /* AsyncFlags.Pending */) {
                    trackPendingStart();
                }
                else {
                    trackPendingEnd();
                }
            }
        }
        if (allChanged === 0) {
            return;
        }
        if ((allChanged & (16 /* AsyncFlags.Value */ | 32 /* AsyncFlags.Error */)) !== 0) {
            this._updatedCount++;
        }
        const subs = this._stateSubs;
        for (const [signalRef, subbedFlags] of subs) {
            if ((subbedFlags & allChanged) !== 0) {
                const signal = signalRef.deref();
                if (signal) {
                    dirtySignal(signal);
                }
                subs.delete(signalRef);
            }
        }
        this._version.update(v => v + 1);
        if (IS_DEV) {
            const tracer = getTracerProxy();
            if (tracer !== undefined && this._signal !== undefined) {
                if (setTrue & 1 /* AsyncFlags.Pending */ && allChanged & 1 /* AsyncFlags.Pending */) {
                    tracer.emit({
                        type: TracerEventType.StartLoading,
                        id: this._signal.tracerMeta.id,
                    });
                }
                else if (setFalse & 1 /* AsyncFlags.Pending */ && allChanged & 1 /* AsyncFlags.Pending */) {
                    tracer.emit({
                        type: TracerEventType.EndLoading,
                        id: this._signal.tracerMeta.id,
                        value: isRelay(this) ? '...' : this._value,
                    });
                }
            }
        }
    }
    _getPending() {
        return (this._flags & 1 /* AsyncFlags.Pending */) !== 0;
    }
    _setPending() {
        if (this._getPending()) {
            return this._awaitSubs;
        }
        this._setFlags(1 /* AsyncFlags.Pending */);
        // We need to dirty the consumers of the promise so that we insert the promise edge
        // into the dirty list of the consumers. This way, when the reactive function is
        // called again, it will see the promise edge and halt computation. This way we
        // ensure that we recompute the promises _in order_, without calling subsequent
        // dirty promises until the previous promise is resolved.
        dirtySignalConsumers(this._awaitSubs);
        return (this._awaitSubs = new Map());
    }
    _clearPending() {
        this._setValue(this._value);
    }
    async _setPromise(promise) {
        // Store the current promise so we can check if it's the same promise in the
        // then handlers. If it's not the same promise, it means that the promise has
        // been recomputed and replaced, so we should not update state.
        this._promise = promise;
        const flags = this._flags;
        let awaitSubs = this._awaitSubs;
        try {
            // If we were not already pending, we need to propagate the dirty state to any
            // consumers that were added since the promise was resolved last.
            if ((flags & 1 /* AsyncFlags.Pending */) === 0) {
                awaitSubs = this._setPending();
            }
            const nextValue = await promise;
            if (promise !== this._promise) {
                return;
            }
            this._setValue(nextValue, awaitSubs);
        }
        catch (nextError) {
            if (promise !== this._promise) {
                return;
            }
            this._setError(nextError, awaitSubs);
        }
    }
    _setValue(nextValue, awaitSubs = this._awaitSubs) {
        let flags = this._flags;
        let value = this._value;
        let notifyFlags = 0;
        if ((flags & 8 /* AsyncFlags.Ready */) === 0 || this._equals(value, nextValue) === false) {
            this._value = value = nextValue;
            notifyFlags = 16 /* AsyncFlags.Value */;
        }
        if (flags & 2 /* AsyncFlags.Rejected */) {
            notifyFlags = 32 /* AsyncFlags.Error */;
            this._error = undefined;
        }
        if ((flags & 1 /* AsyncFlags.Pending */) !== 0) {
            this._scheduleSubs(awaitSubs, notifyFlags !== 0);
        }
        else if (notifyFlags !== 0) {
            dirtySignalConsumers(awaitSubs);
        }
        this._awaitSubs = awaitSubs = new Map();
        this._setFlags(4 /* AsyncFlags.Resolved */ | 8 /* AsyncFlags.Ready */, 1 /* AsyncFlags.Pending */ | 2 /* AsyncFlags.Rejected */, notifyFlags);
        const pending = this._pending;
        this._pending = [];
        const updatedAt = this._updatedCount;
        for (const { ref, edge, resolve } of pending) {
            resolve?.(value);
            if (ref !== undefined) {
                edge.updatedAt = updatedAt;
                awaitSubs.set(ref, edge);
            }
        }
        const signal = this._signal;
        if (signal !== undefined) {
            disconnectSignal(signal);
        }
    }
    _setError(nextError, awaitSubs = this._awaitSubs) {
        let error = this._error;
        let notifyFlags = 0;
        if (error !== nextError) {
            this._error = error = nextError;
            notifyFlags = 32 /* AsyncFlags.Error */;
        }
        if ((this._flags & 1 /* AsyncFlags.Pending */) !== 0) {
            this._scheduleSubs(awaitSubs, notifyFlags !== 0);
        }
        else if (notifyFlags !== 0) {
            dirtySignalConsumers(awaitSubs);
        }
        this._awaitSubs = awaitSubs = new Map();
        this._setFlags(2 /* AsyncFlags.Rejected */, 1 /* AsyncFlags.Pending */ | 4 /* AsyncFlags.Resolved */, notifyFlags);
        const pending = this._pending;
        this._pending = [];
        const updatedAt = this._updatedCount;
        for (const { ref, edge, reject } of pending) {
            reject?.(error);
            if (ref !== undefined) {
                edge.updatedAt = updatedAt;
                awaitSubs.set(ref, edge);
            }
        }
        const signal = this._signal;
        if (signal !== undefined) {
            disconnectSignal(signal);
        }
    }
    _scheduleSubs(awaitSubs, dirty) {
        /**
         * Await subscribers represent `await` statements, which is why they have a bit
         * of a different notification path in general. But this area in particular is
         * very nuanced.
         *
         * Basically, there are two places where an Await subscriber can be added:
         *
         * 1. `.then()` on the ReactivePromise, e.g. a real `await` statement
         * 2. `checkSignal` on a signal that is a dependency of the ReactivePromise
         *
         * In the first case, we're actually executing the parent function, so when it
         * halts on that `await` statement, it'll automatically start running again
         * when we resolve the promise. This is why we push that subscriber into `pending`,
         * because we don't need to notify that promise until the _next change_.
         *
         * In the second case, we're not actually executing the parent function, we're just
         * checking the signal's dependencies. So to continue "executing" the parent
         * function, we need to schedule it to continue where we left off.
         *
         * So the `_awaitSubs` map we're capturing here is _just_ the subscribers
         * added in the second case, which is why we schedule them eagerly.
         */
        const newState = dirty ? 2 /* ReactiveFnState.Dirty */ : 4 /* ReactiveFnState.PendingDirty */;
        for (const ref of awaitSubs.keys()) {
            const signal = ref.deref();
            if (signal === undefined) {
                continue;
            }
            signal._state = newState;
            scheduleAsyncPull(signal);
        }
    }
    get value() {
        this._consumeFlags(16 /* AsyncFlags.Value */);
        return this._value;
    }
    get error() {
        this._consumeFlags(32 /* AsyncFlags.Error */);
        return this._error;
    }
    get isPending() {
        this._consumeFlags(1 /* AsyncFlags.Pending */);
        return (this._flags & 1 /* AsyncFlags.Pending */) !== 0;
    }
    get isRejected() {
        this._consumeFlags(2 /* AsyncFlags.Rejected */);
        return (this._flags & 2 /* AsyncFlags.Rejected */) !== 0;
    }
    get isResolved() {
        this._consumeFlags(4 /* AsyncFlags.Resolved */);
        return (this._flags & 4 /* AsyncFlags.Resolved */) !== 0;
    }
    get isReady() {
        this._consumeFlags(8 /* AsyncFlags.Ready */);
        return (this._flags & 8 /* AsyncFlags.Ready */) !== 0;
    }
    get isSettled() {
        this._consumeFlags(6 /* AsyncFlags.Settled */);
        return (this._flags & 6 /* AsyncFlags.Settled */) !== 0;
    }
    then(onfulfilled, onrejected) {
        let flags = this._flags;
        // Create a new Promise that will be returned
        return new Promise((resolve, reject) => {
            let ref, edge;
            const currentConsumer = getCurrentConsumer();
            if (currentConsumer !== undefined) {
                if ((flags & 128 /* AsyncFlags.isRelay */) !== 0) {
                    this._connect();
                    flags = this._flags;
                }
                ref = currentConsumer.ref;
                const prevEdge = this._awaitSubs.get(ref) ?? findAndRemoveDirty(currentConsumer, this);
                edge = createEdge(prevEdge, 1 /* EdgeType.Promise */, this, this._updatedCount, currentConsumer.computedCount);
            }
            // Create wrapper functions that will call the original callbacks and then resolve/reject the new Promise
            const wrappedFulfilled = onfulfilled
                ? (value) => {
                    try {
                        const result = onfulfilled(value);
                        resolve(result);
                    }
                    catch (error) {
                        reject(error);
                    }
                }
                : resolve;
            const wrappedRejected = onrejected
                ? (reason) => {
                    try {
                        const result = onrejected(reason);
                        resolve(result);
                    }
                    catch (error) {
                        reject(error);
                    }
                }
                : reject;
            if (flags & 1 /* AsyncFlags.Pending */) {
                this._pending.push({ ref, edge, resolve: wrappedFulfilled, reject: wrappedRejected });
            }
            else {
                if (flags & 4 /* AsyncFlags.Resolved */) {
                    wrappedFulfilled(this._value);
                }
                else if (flags & 2 /* AsyncFlags.Rejected */) {
                    wrappedRejected(this._error);
                }
                if (ref) {
                    this._awaitSubs.set(ref, edge);
                }
            }
        });
    }
    catch(onrejected) {
        return this.then(null, onrejected);
    }
    finally(onfinally) {
        return this.then(value => {
            onfinally?.();
            return value;
        }, reason => {
            onfinally?.();
            throw reason;
        });
    }
    get [Symbol.toStringTag]() {
        return `ReactivePromise`;
    }
}
const REACTIVE_PROMISE_SET = new WeakSet();
/**
 * This is a utility function to mark a value as a ReactivePromise, primarily to enable _proxy_
 * wrapping of ReactivePromises to add additional functionality (see: Signalium Query)
 */
export function setReactivePromise(value) {
    REACTIVE_PROMISE_SET.add(value);
}
export function isReactivePromise(value) {
    return REACTIVE_PROMISE_SET.has(value);
}
export function isRelay(obj) {
    return isReactivePromise(obj) && (obj['_flags'] & 128 /* AsyncFlags.isRelay */) !== 0;
}
export function createPromise(promise, signal) {
    const p = new ReactivePromiseImpl();
    p['_signal'] = signal;
    p['_equals'] = signal.def.equals;
    p['_initFlags'](1 /* AsyncFlags.Pending */);
    p['_setPromise'](promise);
    return p;
}
export function createRelay(activate, scope, opts) {
    const p = new ReactivePromiseImpl();
    let active = false;
    let currentSub;
    const unsubscribe = (options) => {
        if (typeof currentSub === 'function') {
            currentSub();
        }
        else if (currentSub !== undefined) {
            currentSub.deactivate?.(options);
        }
        const signal = p['_signal'];
        // Reset the signal state, preparing it for next activation
        signal.subs = new Map();
        signal._state = 2 /* ReactiveFnState.Dirty */;
        active = false;
        currentSub = undefined;
    };
    const state = {
        get isPending() {
            return (p['_flags'] & 1 /* AsyncFlags.Pending */) !== 0;
        },
        get value() {
            return p['_value'];
        },
        set value(value) {
            p['_setValue'](value);
        },
        setPromise: (promise) => {
            p['_setPromise'](promise);
        },
        setError: (error) => {
            p['_setError'](error);
        },
    };
    const def = {
        compute: () => {
            if (active === false) {
                currentSub = activate(state);
                active = true;
            }
            else if (typeof currentSub === 'function' || currentSub === undefined) {
                currentSub?.();
                currentSub = activate(state);
            }
            else {
                currentSub.update?.();
            }
            return unsubscribe;
        },
        equals: DEFAULT_EQUALS,
        isRelay: true,
        paramKey: opts?.paramKey,
        id: opts?.id,
        desc: opts?.desc,
        tracer: undefined,
    };
    p['_signal'] = createReactiveSignal(def, [], undefined, scope);
    p['_equals'] = equalsFrom(opts?.equals);
    p['_initFlags'](128 /* AsyncFlags.isRelay */ | 1 /* AsyncFlags.Pending */);
    return p;
}
export function createTask(task, scope, opts) {
    const p = new ReactivePromiseImpl();
    const { fn } = createCallback(task, scope);
    p['_equals'] = equalsFrom(opts?.equals);
    p['_initFlags'](64 /* AsyncFlags.isRunnable */);
    const throwIfRunning = opts?.throwIfRunning === true;
    p['run'] = ((...args) => {
        if (throwIfRunning && (p['_flags'] & 1 /* AsyncFlags.Pending */) !== 0) {
            throw new Error('Task is already running');
        }
        p._setPromise(fn(...args));
        return p;
    });
    return p;
}
// We mirror the standard `Promise` pattern in lib.es5.d.ts: declare a value
// (the constructor) and a generic type with the same name, side-by-side, in
// the same module. This way they merge into one identifier — re-exporting
// `{ ReactivePromise }` from `index.ts` carries both meanings.
//
// - The value is the implementation class cast to `ReactivePromiseConstructor`
//   (so `new ReactivePromise<T>()`, the statics, and `instanceof` are all
//   typed against the public `Pending | Ready` union with internals hidden).
// - The type is just the public discriminated union from `types.ts`.
export const ReactivePromise = ReactivePromiseImpl;
